import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PaisleyDivider, Mandala } from "@/lib/motifs";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

const Footer = () => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await subscribeNewsletter(email);
            toast.success("Welcome to the Kanganya family");
            setEmail("");
        } catch {
            toast.error("Please enter a valid email");
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer className="relative overflow-hidden mt-24" style={{ backgroundColor: "var(--maroon)" }} data-testid="site-footer">
            <Mandala className="absolute -top-16 -right-16 opacity-10 slow-spin" size={340} color="#C9A227" />
            <Mandala className="absolute -bottom-24 -left-20 opacity-10" size={320} color="#C9A227" />

            {/* Main footer */}
            <div className="container-x pt-20 pb-12 relative grid md:grid-cols-3 gap-12">
                {/* Brand + newsletter */}
                <div>
                    <p className="font-heading text-2xl tracking-[0.15em] text-[color:var(--ivory)] text-center">KANGANYA</p>
                    <div className="flex items-center justify-center gap-2 mt-1.5">
                        <span className="w-6 h-px bg-[color:var(--gold)]" />
                        <span className="w-1.5 h-1.5 rotate-45 bg-[color:var(--gold)]" />
                        <span className="w-6 h-px bg-[color:var(--gold)]" />
                    </div>
                    <p className="font-devanagari text-lg text-[color:var(--ivory)] mt-1 leading-none text-center">कंगन्या</p>
                    <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-[color:var(--gold)] mt-1.5 text-center">Bangles · Bridal · Heritage</p>
                    <p className="font-body text-sm mt-4 max-w-sm leading-relaxed text-[color:var(--ivory)] opacity-80">
                        Every bangle carries a story, shaped by Kanganya hands through generations.
                    </p>

                    <p className="font-ui text-xs tracking-widest-x uppercase text-[color:var(--gold)] mt-9 mb-3">
                        Join The Kanganya Circle
                    </p>
                    <form onSubmit={submit} className="flex gap-3 items-stretch" data-testid="newsletter-form">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email address"
                            className="flex-1 bg-transparent border border-[color:var(--gold)]/40 text-[color:var(--ivory)] px-5 py-3.5 font-body text-sm placeholder:text-[color:var(--ivory)]/50 focus:outline-none focus:border-[color:var(--gold)] transition-colors duration-300"
                            data-testid="newsletter-email-input"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            aria-label="Subscribe"
                            className="shrink-0 w-14 flex items-center justify-center border border-[color:var(--gold)]/40 text-[color:var(--ivory)] hover:bg-[color:var(--gold)] hover:text-[color:var(--maroon)] transition-colors duration-300 disabled:opacity-60"
                            data-testid="newsletter-submit-button"
                        >
                            <ArrowRight size={18} strokeWidth={1.5} />
                        </button>
                    </form>
                </div>

                {/* Explore */}
                <div>
                    <p className="font-heading text-xl text-[color:var(--ivory)] mb-5">Explore</p>
                    <ul className="space-y-2.5">
                        {[
                            ["Home", "/"],
                            ["Shop Bangles", "/shop"],
                            ["Our Story", "/story"],
                            ["Get In Touch", "#"],
                            ["Questions", "#"],
                        ].map(([label, href]) => (
                            <li key={label}>
                                <Link to={href} className="font-body text-sm text-[color:var(--gold)] opacity-80 hover:opacity-100 transition-colors duration-300">{label}</Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Support */}
                <div>
                    <p className="font-heading text-xl text-[color:var(--ivory)] mb-5">Support</p>
                    <ul className="space-y-2.5">
                        {[
                            ["Terms & Conditions", "/terms"],
                            ["Returns & Exchanges", "/returns"],
                            ["Size Guide", "/size-guide"],
                            ["Privacy Policy", "/privacy"],
                            ["Track My Order", "/track"],
                        ].map(([label, href]) => (
                            <li key={label}>
                                <Link to={href} className="font-body text-sm text-[color:var(--gold)] opacity-80 hover:opacity-100 transition-colors duration-300">{label}</Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="relative border-t border-[color:var(--gold)]/25">
                <div className="container-x py-6">
                    <PaisleyDivider className="mb-4 opacity-60" />
                    <p className="text-center font-body text-xs text-[color:var(--ivory)] opacity-60">
                        © {new Date().getFullYear()} Kanganya
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;